import React from "react";
import clsx from "clsx";
import styling from "./Text.module.scss";
import {
	type FontAlign,
	type FontFamily,
	type FontWeight,
	type FontWrap,
	type TextTag,
	type WordBreak,
	type TypographyType,
	type SizeToken,
} from "src/models/types/common.types";
import { TextTagMap } from "src/models/records/typography.records";
import DOMPurify from "dompurify";

export namespace Text {
	export type Props<T extends TextTag> = Omit<React.ComponentProps<T>, "color"> & {
		id?: string;
		className?: string;
		children?: React.ReactNode;
		as?: T;
		ref?: any;

		family?: FontFamily;
		size?: SizeToken;
		weight?: FontWeight;
		align?: FontAlign;
		wrap?: FontWrap;
		wordBreak?: WordBreak;
		allowEvents?: boolean;
		type?: TypographyType;

		slot?: string;
		style?: React.CSSProperties;
		color?: "inherit" | "contrast" | "secondary";
		disabled?: boolean;
		ellipsis?: boolean;
		uppercase?: boolean;
		lowercase?: boolean;
		allowHtmlStyling?: boolean;
	};
}

export const Text = <T extends TextTag = "p">(props: Text.Props<T>) => {
	const {
		id,
		className,
		children,
		ref,
		as,
		color = "contrast",
		disabled,
		type = "body-medium",
		family,
		size,
		weight,
		align,
		wrap,
		wordBreak,
		allowEvents,
		allowHtmlStyling = false,
		ellipsis = false,
		uppercase,
		lowercase,
	} = props;
	const Tag = as ? as : TextTagMap[type || "body-medium"];

	const _requiresSanitization = (): boolean => {
		if (!children || !allowHtmlStyling) {
			return false;
		}

		return typeof children === "string" || typeof children === "object";
	};

	const _configuredProperties = () => {
		return {
			id,
			ref,
			className: clsx(`${styling["text"]}`, {
				[styling[`color--${disabled ? "disabled" : color}`]]: color || !!disabled,
				[styling[`typography--${type}`]]: !!type,
				[styling[`font-family--${family}`]]: !!family,
				[styling[`font-size--${size}`]]: !!size,
				[styling[`font-weight--${weight}`]]: !!weight,
				[styling[`align--${align}`]]: !!align,
				[styling[`white-space--${wrap}`]]: !!wrap,
				[styling[`word-break--${wordBreak}`]]: !!wordBreak,
				[styling[`no-events`]]: !allowEvents,
				[styling[`lowercase`]]: lowercase,
				[styling[`uppercase`]]: uppercase,
				[styling[`ellipsis`]]: !!ellipsis,
				[`${className}`]: className,
			}),
		};
	};

	if (_requiresSanitization()) {
		const purifiedHtml = DOMPurify.sanitize(`${children}`);
		return <Tag {..._configuredProperties()} dangerouslySetInnerHTML={{ __html: purifiedHtml }} />;
	} else {
		return <Tag {..._configuredProperties()}>{children}</Tag>;
	}
};
